import { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import classNames from "classnames";
import ErrorMessage from "../ErrorMessage";
import { X } from "../Icons/general-interface-1";
import styles from "./index.module.scss";

type PropsType = {
  section?: string;
  name: string;
  options: Array<{
    label: string;
    value: string;
    tooltip?: ReactNode;
  }>;
  horizontal?: boolean;
  label?: string;
  description?: string;
  disabled?: boolean;
  requiredText?: string;
  tooltip?: ReactNode;
  className?: string;
  readOnly?: boolean; // readOnly = disabled with a different style
};

const RadioGroup = ({
  section,
  name,
  requiredText,
  options,
  disabled,
  horizontal,
  label,
  description,
  tooltip,
  className,
  readOnly,
}: PropsType) => {
  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useFormContext();
  const nameDotFormat = (() => {
    if (section) {
      return `${section}.${name}`;
    }
    return `${name}`;
  })();

  const error =
    section && errors[section] ? errors[section][name] : errors[name];
  const selectedValue = useWatch({ name: nameDotFormat });

  const atLeastOne = () =>
    getValues(nameDotFormat) !== null ? true : requiredText;

  const icon = <X className={styles.icon} />;

  const clearRadioValue = () => {
    setValue(nameDotFormat, null);
    trigger(nameDotFormat);
  };

  return (
    <div className={className}>
      {label && (
        <span className={styles.labelText}>
          {label}
          {tooltip}
        </span>
      )}
      {description && (
        <p className={styles.descriptionText}>
          {description} {!label && tooltip}
        </p>
      )}

      {error && requiredText && (
        <ErrorMessage className={styles.radioError} field={name}>
          {error.message}
        </ErrorMessage>
      )}

      <fieldset
        className={classNames(styles.radioGroupFieldset, {
          [styles.horizontal]: horizontal,
        })}
      >
        <>
          {options.map((option) => (
            <label
              className={classNames(classNames, styles.radioInputLabel, {
                [styles.radioInputLabelDisabled]:
                  disabled || (readOnly && option.value !== selectedValue),
              })}
              htmlFor={`${nameDotFormat}.${option.value}`}
              key={option.value}
            >
              <input
                type="radio"
                className={classNames(classNames, styles.radioInput, {
                  [styles.radioInputDisabled]: disabled,
                  [styles.radioInputReadonly]: readOnly,
                })}
                id={`${nameDotFormat}.${option.value}`}
                value={option.value}
                disabled={readOnly || disabled}
                {...register(nameDotFormat, { validate: atLeastOne })}
              />
              <span className={styles.radioInputLabelText}>
                {option.label} {option.tooltip}
              </span>
            </label>
          ))}
          {!disabled && !readOnly && selectedValue != null && (
            <button
              className={styles.resetButton}
              type="button"
              onClick={clearRadioValue}
            >
              {icon} Clear selection
            </button>
          )}
        </>
      </fieldset>
    </div>
  );
};

export default RadioGroup;
